/**
 * SupplierModal Component
 *
 * Form modal for creating and editing suppliers (parties).
 * Built on {@link FormModal} from ui-toolkit components; the fields and the ARES register
 * lookups live in {@link SupplierFormFields} / {@link useSupplierForm}, shared with the
 * full-page party editor.
 *
 * Uses `useTranslation('modules.modals.supplier')` for all labels.
 */
import * as React from 'react';
import { useState } from 'react';
import type { TestIdProps } from '../../utils';
import { FormModal } from '../../components/FormModal';
import { useTranslation } from '../../providers/UIProvider';
import {
  SupplierFormFields,
  useSupplierForm,
  type AresLookupData,
  type SupplierData,
  type SupplierFormData
} from '../forms/supplier';

export type {
  AresLookupAddress,
  AresLookupData,
  SupplierData,
  SupplierFormData
} from '../forms/supplier';

export interface SupplierModalProps extends TestIdProps {
  open: boolean;
  onClose: () => void;
  onSave: (data: SupplierFormData) => Promise<void>;
  supplier?: SupplierData | null;
  validateEmail?: (email: string) => boolean;
  /**
   * When `true` the "Active" checkbox is hidden and `isActive` is always `true`.
   * Use when creating a partner inline from invoice detail — a new partner
   * created in context should always be active.
   */
  hideIsActive?: boolean;
  /**
   * ARES lookup by IČO. Resolves `null` when not found, rejects on service error.
   * When omitted, the ARES search field is hidden.
   */
  onAresLookup?: (ico: string) => Promise<AresLookupData | null>;
}

export const SupplierModal: React.FC<SupplierModalProps> = ({
  open,
  onClose,
  onSave,
  supplier,
  validateEmail,
  hideIsActive = false,
  onAresLookup
}) => {
  const t = useTranslation('modules.modals.supplier');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const form = useSupplierForm({ supplier, validateEmail, onAresLookup, active: open });

  const handleClose = () => {
    form.reset();
    setIsSubmitting(false);
    onClose();
  };

  const handleSubmit = async () => {
    if (!form.validateForm() || isSubmitting) return;
    setIsSubmitting(true);
    try {
      await onSave(form.formData);
      setIsSubmitting(false);
      handleClose();
    } catch {
      setIsSubmitting(false);
    }
  };

  return (
    <FormModal
      open={open}
      onClose={handleClose}
      onSubmit={handleSubmit}
      title={supplier ? t('titleEdit') : t('titleAdd')}
      submitLabel={
        isSubmitting ? t('actions.saving') : supplier ? t('actions.save') : t('actions.create')
      }
      cancelLabel={t('actions.cancel')}
      loading={isSubmitting}
      error={form.formError}
      size='xl'
    >
      <SupplierFormFields form={form} hideIsActive={hideIsActive} />
    </FormModal>
  );
};

SupplierModal.displayName = 'SupplierModal';
